/**
 * Account → Payment methods (M4). Lists the user's saved cards (reactive Firestore read of safe metadata),
 * and lets them add a card via the PCI Booking capture iframe, set a default, or delete one. All mutations
 * go through wallet flows; the PCI token never touches the client.
 *
 * These cards pay HOTELS, and nothing else (`SubmitHotelBookingFlow` is their only consumer — flight
 * bookings do not touch them). The membership subscription is charged by Stripe against a card Elara
 * never sees, held in Stripe's vault and changed only through its portal.
 *
 * Since ELARA-1014 a member has two places to enter a card, and the two are not interchangeable: a
 * card added here will never pay a membership invoice, and changing the membership card does nothing
 * for a hotel booking. Saying which is which is the whole job of the note below — an unpaid
 * membership "fixed" by adding a card on this page is a member who thinks they have paid and has not.
 */

import type { CapturedCard } from '#views/wallet/CardCaptureFrame.jsx';

import { useState } from 'react';

import { ROUTES } from '@elara/client-core/constants';

import { useCurrentUserProfile, usePaymentMethodsState, useRouterState } from '#states/hooks.js';
import { Breadcrumbs } from '#ui/Breadcrumbs.js';
import { CardBrandIcon } from '#ui/CardBrandIcon.jsx';
import { Alert, AlertDescription, AlertTitle, Badge, Button, Loader } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';
import { CardCaptureFrame } from '#views/wallet/CardCaptureFrame.jsx';

import { useBillingPortal } from './use-billing-portal.js';

export function PaymentMethodsPage() {
  const state = usePaymentMethodsState();
  const routerState = useRouterState();
  const profile = useCurrentUserProfile();
  const billingPortal = useBillingPortal(profile?.subscription);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function handleCaptured(card: CapturedCard) {
    await state.addCard(card);
    setAdding(false);
  }

  async function runBusy(id: string, action: () => Promise<void>) {
    setBusyId(id);
    try {
      await action();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppLayout
      backButtonRoute={ROUTES.ACCOUNT}
      title={<AppLayoutTitle title="Payment Methods" />}
      transitionKey={routerState.currentRoute?.path}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-16 flex flex-col gap-6">
            <div className="flex flex-col gap-3 mb-2">
              <Breadcrumbs
                items={[{ label: 'Account', to: ROUTES.ACCOUNT }, { label: 'Payment Methods' }]}
                className="mb-3"
              />

              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-3 flex-1">
                  <h1 className="text-2xl xl:text-3xl font-display font-light">Payment Methods</h1>
                  <p className="text-muted-foreground">Cards you’ve saved for booking hotels through Elara.</p>
                </div>

                {!adding && (
                  <Button variant="default" onClick={() => setAdding(true)}>
                    <MaterialIcon icon="add_card" /> Add a card
                  </Button>
                )}
              </div>

              {/* Only shown to a member who actually HAS a second card to confuse this one with —
                  `canManage` is false for a member with no subscription and for a comped one, and
                  neither has a membership card to manage. See `use-billing-portal.ts`. */}
              {billingPortal.canManage && (
                <Alert
                  variant="info"
                  className="flex gap-3"
                  // `Alert` defaults to `role="alert"`, an assertive live region. Right for something
                  // that just went wrong; wrong for a note that is simply always here, which a screen
                  // reader would then announce over whatever the member was doing.
                  role="note"
                >
                  <MaterialIcon icon="verified" size={IconSizes.sm} className="shrink-0" />
                  <div>
                    <AlertTitle>Your membership is billed separately</AlertTitle>
                    <AlertDescription>
                      Cards saved here are only used when you book a hotel. Your Elara membership is charged by Stripe
                      to a card held securely on its side — adding a card here won’t change it.
                      <Button
                        variant="link"
                        className="mt-1 h-auto cursor-pointer p-0 text-inherit underline"
                        disabled={billingPortal.isOpening}
                        onClick={() => {
                          void billingPortal.openPortal();
                        }}
                      >
                        {billingPortal.isOpening ? 'Opening billing settings…' : 'Manage your membership card'}
                        <MaterialIcon icon="arrow_outward" size={IconSizes.sm} />
                      </Button>
                    </AlertDescription>
                  </div>
                </Alert>
              )}
            </div>
            {!state.isReady ? (
              <div className="flex items-center justify-center py-12">
                <Loader loading />
              </div>
            ) : (
              <>
                {state.cards.length === 0 && !adding && (
                  <div className="px-6 py-8 flex flex-col justify-center items-center border rounded-md gap-2 text-sm text-muted-foreground text-center">
                    <MaterialIcon icon="credit_card_off" size={IconSizes.md} />
                    <div>No saved cards yet.</div>
                  </div>
                )}

                {adding && (
                  <CardCaptureFrame
                    mintSession={async () => state.mintCaptureSession(window.location.origin)}
                    onCaptured={handleCaptured}
                    onCancel={() => setAdding(false)}
                  />
                )}

                {state.cards.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {state.cards.map((card) => (
                      <div
                        key={card.id}
                        className={`flex items-center gap-4 px-4 py-4 rounded-md border bg-background ${card.isExpired ? 'opacity-60' : ''}`}
                      >
                        <CardBrandIcon brand={card.brand} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium flex items-center gap-2">
                            <span className="truncate text-sm">{card.displayLabel}</span>
                            {card.isDefault && <Badge variant="outline">Default</Badge>}
                            {card.isExpired && <Badge variant="destructive">Expired</Badge>}
                          </div>
                          <div className={`text-xs ${card.isExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
                            Expires {String(card.expMonth).padStart(2, '0')}/{card.expYear}
                          </div>
                        </div>
                        {!card.isDefault && !card.isExpired && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === card.id}
                            onClick={async () => runBusy(card.id, async () => state.setDefault(card.id))}
                          >
                            Set default
                          </Button>
                        )}
                        <Button
                          variant="icon"
                          size="icon-sm"
                          aria-label="Delete card"
                          disabled={busyId === card.id}
                          onClick={async () =>
                            runBusy(card.id, async () => {
                              if (confirm(`Are you sure you want to delete ${card.displayLabel}?`)) {
                                await state.removeCard(card.id);
                              }
                            })
                          }
                        >
                          <MaterialIcon icon="delete" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
